import jwtDecode from 'jwt-decode';

import EsquemaConfiguracao from '../constants/EsquemaConfiguracao';
import Evolucao from '../constants/Evolucao';
import Sistema from '../constants/Sistema';
import TextHelper from '../helpers/TextHelper';
import ApiUsuario from './ApiUsuario';
import ApiV2 from './ApiV2';
import CursoService from './CursoService';

export default class AuthService {
  static async parseToken(token) {
    const decoded = jwtDecode(token);

    return {
      id_usuario: decoded.user_id,
      id_entidade: decoded.entidade_id,
      st_email: decoded.user_email,
      st_nomecompleto: decoded.user_name,
    };
  }

  static async login(login, senha) {
    // Realizar auth do usuáro
    const { data: dataUser } = await this.authUsuario(login, senha);
    const { token } = dataUser;

    // Realizar auth do sistema
    const { data: authSistema } = await this.authSistema();
    const { token: token_sistema } = authSistema;

    // Dados mapeados do usuário
    const user = await this.parseToken(token);

    return {
      data: {
        user,
        token,
        token_sistema,
      },
    };
  }

  static async loginDireto({ id_entidade, id_usuario, st_urlacesso }) {
    // Realizar auth do usuáro (direto)
    const { data: authV2 } = await ApiV2.post('/auth/autenticacao-direta', {
      id_entidade,
      id_usuario,
      st_urlacesso,
      bl_gravalog: true,
      id_usuariooriginal: id_usuario,
    });

    const { token } = authV2;

    // Realizar auth do sistema
    const { data: authSistema } = await this.authSistema();
    const { token: token_sistema } = authSistema;

    // Dados mapeados do usuário
    const user = await this.parseToken(token);

    return {
      data: {
        user,
        token,
        token_sistema,
      },
    };
  }

  static async authUsuario(login, senha) {
    return ApiV2.post('/auth/login', {
      login,
      senha,
      id_sistema: Sistema.PORTAL_EGRESSO,
    });
  }

  static async authSistema() {
    const { data: authSistema } = await ApiUsuario.post(`/autenticar/sistema/${Sistema.PORTAL_EGRESSO}`, {
      bl_novoSistema: 0,
      st_sistema: 'Portal Egresso',
    });

    const { api_key } = authSistema;

    return ApiUsuario.post(`/login/autenticar/sistema`, { api_key });
  }

  static async recuperarSenha(st_email) {
    return ApiV2.post('/auth/recuperar-senha', { st_email });
  }

  static async atualizarEntidade(id_entidade) {
    return ApiV2.get('/curso-aluno/update-entidade', {
      params: { id_entidade },
    });
  }

  static async getContexts(id_entidade) {
    const { data: entidades } = await CursoService.getCursosAluno(id_entidade);

    const contexts = [];

    for (const entidade of entidades) {
      const { id_entidade, st_nomeentidade, st_urlnovoportal, id_esquemaconfiguracao } = entidade;

      const evolucoes = [
        // Evoluções permitidas de acessar o portal do egresso
        Evolucao.MATRICULA.FORMADO,
        Evolucao.MATRICULA.CERTIFICADO,
        Evolucao.MATRICULA.DIPLOMADO,
        Evolucao.MATRICULA.EGRESSO,
      ];

      // Para a POS, considerar CONCLUINTES
      if (+id_esquemaconfiguracao === EsquemaConfiguracao.POS_GRADUACAO) {
        evolucoes.push(Evolucao.MATRICULA.CONCLUINTE);
      }

      // Verificar se possui matrícula com evolução de EGRESSO
      const matricula = entidade.cursos.find(({ id_evolucao }) => evolucoes.includes(+id_evolucao));

      // Se não possuir matrícula egresso, ir para a próxima entidade
      if (!matricula) {
        continue;
      }

      // Definir nome do contexto
      const st_contexto = EsquemaConfiguracao.getString(id_esquemaconfiguracao) || st_nomeentidade || 'Unyleya';

      if (st_contexto) {
        // Preferencialmente usar url do novo portal. Se não possuir, usar do antigo.
        const st_urlportal = TextHelper.toString(st_urlnovoportal).trim() || TextHelper.toString(entidade?.st_urlportal).trim() || null;

        contexts.push({
          id_entidade: +id_entidade,
          st_nomeentidade,
          st_urlportal,
          st_contexto,
          matricula,
        });
      }
    }

    return { data: contexts };
  }

  /**
   * @param {ContextType} context
   * @param {UserType} user
   * @returns {Promise<null|String>}
   */
  static async getUrlLoginDiretoPortalAluno(context, user) {
    let url = null;

    if (context?.st_urlportal) {
      const { id_usuario, id_entidade, id_curso, id_matricula, st_urlacesso } = user;

      // Gerar URL de login direto no PORTAL DO ALUNO
      // /login/direto/:blGravaLog/:idUsuarioOriginal/:idEntidade/:idUsuario/:stUrlAcesso/:idCurso/:idSalaDeAula/:idMatricula
      url = `${context?.st_urlportal}/login/direto/1/${id_usuario}/${id_entidade}/${id_usuario}/${st_urlacesso}/${id_curso}/0/${id_matricula}`;
    }

    return url;
  }

  static async enviarEmailHashRecuperarSenha({ st_email, id_entidade }) {
    if (!(id_entidade instanceof Array)) {
      id_entidade = [id_entidade];
    }

    return ApiV2.post('/auth/validar-email-criar-hash', {
      st_email,
      id_entidade,
    });
  }

  static async validarHashRecuperarSenha({ st_email, st_hash, id_entidade }) {
    if (!(id_entidade instanceof Array)) {
      id_entidade = [id_entidade];
    }

    return ApiV2.post('/auth/validar-hash', {
      st_email,
      st_hash,
      id_entidade,
    });
  }

  static async salvarNovaSenhaComHash({ st_email, st_hash, st_novasenha, st_confirmacaonovasenha, id_entidade }) {
    if (!(id_entidade instanceof Array)) {
      id_entidade = [id_entidade];
    }

    return ApiV2.post('/auth/redefinir-senha-com-hash', {
      st_email,
      st_hash,
      st_novasenha,
      st_confirmacaonovasenha,
      id_entidade,
    });
  }
}
